/**
 * Everything an FDE configures for one account.
 *
 * Tabbed rather than one long scroll, and the tab order is the order it gets set
 * up in: connect the account, say which agents do callbacks, tell us when it may
 * call, tell us which outcomes are worth calling. Defaults last — they are the
 * thing you tune later, not the thing you fill in first.
 *
 * Every section reads from one `useAccountConfig` query and saves through a
 * mutation that invalidates it, so what is on screen after a save is what the
 * database has, not what the form hoped it wrote.
 *
 * ## Account level here, agent level on the agent's own page (2026-09-03)
 *
 * Arun: *"this setup is not very intuitive … right how all agents are dumped here even ones
 * that i dont want and its hard to keep track and work on the agents we need … think what is
 * account level and waht is agent level and move agent level things to under each agent so
 * its easy to manage."*
 *
 * There used to be seven tabs, three of which — Post-call hook, Routing, Context — each
 * opened with a dropdown of every live agent on the tenant. Three screens asking the same
 * question, none of them remembering the answer.
 *
 * So the split is now explicit, and it is the whole organising idea of this page:
 *
 *  - **Account level, here:** the 8loop connection, the ingest token, calling hours, which
 *    endings are worth calling back and how long afterwards, and the retry defaults.
 *  - **Agent level, on `/accounts/:id/agents/:agentId`:** the post-call hook — the two
 *    questions and the brief that installs it — its dry run, and that agent's channels and
 *    overrides.
 *
 * The Agents tab is the index of the second list. Routing survives inside it as a fold — every
 * channel on the tenant, for "why isn't this one here?".
 *
 * **Context is not on this page at all, and was.** It used to be an account-wide card here: a
 * source → target field map applied to every callback whichever agent dialled. Arun asked three
 * times why that was not per agent, and he was right — the names an agent will accept at call
 * start differ per agent, so an account-wide row is unsettable on most of them. It is now block
 * 2 of each agent's hook setup, where `callback_context` and the `previous_<field>` names are
 * switched on for that agent alone. `?tab=context` still redirects to Agents, which is the way
 * to the agent that owns the answer.
 */

import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/app-shell";
import { AccountSwitcher } from "@/components/account-switcher";
import { PageHeader } from "@/components/ui/page-header";
import { ErrorState } from "@/components/ui/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errorMessage } from "@/lib/api";
import { useAccountConfig, useCallbackAgents } from "@/lib/queries";
import { SetupChecklist } from "@/pages/settings/setup-checklist";
import { SetupSection } from "@/pages/settings/setup-section";
import { AgentsSection } from "@/pages/settings/agents-section";
import { CallingHoursSection } from "@/pages/settings/calling-hours-section";
import { OutcomesSection } from "@/pages/settings/outcomes-section";
import { DefaultsSection } from "@/pages/settings/defaults-section";

const TABS = [
  { value: "setup", label: "Setup" },
  // Second, not last: nothing else on this page matters until 8loop is actually telling
  // the app that a call ended, and that is installed per agent. An account with perfect
  // calling hours and no post-call hook on any agent has an empty queue forever.
  { value: "agents", label: "Agents" },
  { value: "hours", label: "Calling hours" },
  { value: "outcomes", label: "Outcomes" },
  { value: "defaults", label: "Retries & defaults" },
] as const;

/** Where the three tabs that became agent-level went. */
const RETIRED_TABS: Record<string, string> = {
  hook: "agents",
  routing: "agents",
  context: "agents",
};

export function SettingsPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [params, setParams] = useSearchParams();
  const { data, isPending, isError, error, refetch } = useAccountConfig(accountId);
  /*
    Here rather than inside the checklist, because the Agents tab needs the same query and one
    copy in the cache is what keeps a save on an agent's page from leaving this list stale.
  */
  const agents = useCallbackAgents(accountId);

  /*
    Bookmarks, and every `?tab=` link written before the restructure, still land somewhere
    real. Without this a saved link to the old Post-call hook tab renders an empty page with
    no tab selected — which looks like the feature was deleted rather than moved.
  */
  const requested = params.get("tab") ?? "setup";
  const tab = RETIRED_TABS[requested] ?? (TABS.some((t) => t.value === requested) ? requested : "setup");

  const goToTab = (next: string) => {
    const p = new URLSearchParams(params);
    p.set("tab", next);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    document.title = data?.account.name
      ? `Configuration · ${data.account.name}`
      : "Configuration";
  }, [data?.account.name]);

  return (
    <AppShell active="settings">
      <div className="flex flex-col h-full w-full max-w-full">
        <PageHeader
          breadcrumb={[
            { label: "Accounts", href: "/accounts" },
            { label: data?.account.name ?? "Account" },
          ]}
          title="Configuration"
          description="How this account's callbacks get scheduled, dialled and retried. Every setting here applies to this tenant only."
          actionsSlot={<AccountSwitcher />}
        />

        <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
          {isError ? (
            <ErrorState
              title="Couldn't load this account's configuration"
              description={errorMessage(error)}
              onRetry={() => refetch()}
            />
          ) : isPending ? (
            <div className="space-y-4">
              <Skeleton className="h-9 w-[660px]" />
              <Skeleton className="h-64 w-full max-w-3xl rounded-xl" />
              <Skeleton className="h-48 w-full max-w-3xl rounded-xl" />
            </div>
          ) : (
            <Tabs value={tab} onValueChange={goToTab}>
              {/* Above the tabs, because "what do I still have to do?" is the question
                  the page is opened with and no single tab can answer it. */}
              <SetupChecklist config={data} agents={agents.data} onGoToTab={goToTab} />

              <TabsList className="mb-6">
                {/* Tabs with no hover response at all: the only way to tell the inactive
                    ones apart from the heading above them was to click one. The active tab
                    keeps its own background on hover rather than lightening, so hovering it
                    never reads as "not selected". */}
                {TABS.map((t) => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className="hover:bg-background/60 hover:text-foreground data-[state=active]:hover:bg-background"
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="max-w-3xl space-y-6 pb-10">
                <TabsContent value="setup">
                  <SetupSection accountId={accountId!} config={data} />
                </TabsContent>
                <TabsContent value="agents">
                  <AgentsSection accountId={accountId!} config={data} />
                </TabsContent>
                <TabsContent value="hours">
                  <CallingHoursSection accountId={accountId!} config={data} />
                </TabsContent>
                <TabsContent value="outcomes">
                  <OutcomesSection accountId={accountId!} config={data} />
                </TabsContent>
                <TabsContent value="defaults">
                  <DefaultsSection accountId={accountId!} config={data} />
                </TabsContent>
              </div>
            </Tabs>
          )}
        </div>
      </div>
    </AppShell>
  );
}
